<main>
  <loading-overlay [isLoading]="isLoading"></loading-overlay>

  <!-- Header -->
  <header>
    <div class="headline">
      <h1>
        {{
          originalFinishedGoodsTransfer
            ? ('Finished_Goods_Transfer_Details_Title' | translateWF: 'Finished Goods Transfer Details') + ': ' + originalFinishedGoodsTransfer.number
            : ('Finished_Goods_Transfer_Details_New' | translateWF: 'New Finished Goods Transfer')
        }}
      </h1>
      @if (originalFinishedGoodsTransfer) {
        <csl-dropdown [enumObject]="statusEnum" [enumValueOptions]="roleStatusOptions" [formControl]="status"></csl-dropdown>
      } @else {
        <csl-dropdown [enumObject]="statusEnum" [enumValueOptions]="{}" [formControl]="status"></csl-dropdown>
      }
    </div>
    <div class="buttons">
      <p-button
        label="{{ 'Finished_Goods_Transfer_Details_Save' | translateWF: 'Save' }}"
        (click)="insertFinishedGoodsTransfer()"
        *ngIf="!originalFinishedGoodsTransfer || (canEdit() && !originalFinishedGoodsTransfer)"
        [disabled]="isLoading"
      />
      <p-button label="{{ 'Finished_Goods_Transfer_Details_Cancel' | translateWF: 'Cancel' }}" (click)="cancel()" variant="outlined" />
    </div>
  </header>

  <div class="content">
    <!-- Production Card Selection (only for new) -->
    <p-panel header="Overview">
      <p-card>
        <div class="col">
          @if (!originalFinishedGoodsTransfer) {
            <div class="form-box">
              <label for="">{{ 'Select_Production_Card' | translateWF: 'Select Production Card' }}</label>
              <p-autoComplete
                [formControl]="productionCard"
                #productionCardSearchRef="cslServerSearch"
                [cslServerSearch]="fetchProductionCards"
                [suggestions]="productionCardSearchRef.results()"
                [forceSelection]="true"
                (onSelect)="onProductionCardSelected()"
                optionLabel="productionCardNumber"
                [dropdown]="true"
              />
            </div>

            <!-- Show source location selector after production card is selected -->
            @if (productionCard.value?.id) {
              <div class="form-box">
                <label>{{ 'Source_Location' | translateWF: 'Source Location' }}</label>
                @if (sourceLocationOptions().length === 1) {
                  <p-tag [value]="getSourceLocationLabel(selectedSourceLocation())" />
                } @else {
                  <p-select
                    [options]="sourceLocationOptions()"
                    [ngModel]="selectedSourceLocation()"
                    (ngModelChange)="selectedSourceLocation.set($event)"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="{{ 'Select_Source' | translateWF: 'Select source location' }}"
                    styleClass="w-auto"
                  />
                }
              </div>
            }
          } @else {
            <div class="grid-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <span class="text-gray-500">{{ 'Production_Card' | translateWF: 'Production Card' }}</span>
                <p class="font-semibold">{{ productionCard.value?.productionCardNumber }}</p>
              </div>
              @if (workOrder()) {
                <div>
                  <span class="text-gray-500">{{ 'Work_Order' | translateWF: 'Work Order' }}</span>
                  <p class="font-semibold">{{ workOrder()?.orderNumber }}</p>
                </div>
                <div>
                  <span class="text-gray-500">{{ 'Client' | translateWF: 'Client' }}</span>
                  <p class="font-semibold">{{ workOrder()?.customer?.name || workOrder()?.customerReference }}</p>
                </div>
                <div>
                  <span class="text-gray-500">{{ 'Brand' | translateWF: 'Brand' }}</span>
                  <p class="font-semibold">{{ workOrder()?.brand?.name || workOrder()?.brandReference }}</p>
                </div>
                <div>
                  <span class="text-gray-500">{{ 'Color' | translateWF: 'Color' }}</span>
                  <p class="font-semibold">{{ getColorName(productionCard.value?.colorReference ?? '') }}</p>
                </div>
              }
            </div>
          }
        </div>
      </p-card>
    </p-panel>

    <!-- Source Location Display (for existing transfers - read only) -->
    @if (originalFinishedGoodsTransfer && originalFinishedGoodsTransfer.fromLocationCode) {
      <p-card>
        <div class="row-sm-start-center">
          <span class="font-semibold">{{ 'Source_Location' | translateWF: 'Source Location' }}:</span>
          <p-tag [value]="getSourceLocationLabel(originalFinishedGoodsTransfer.fromLocationCode)" />
        </div>
      </p-card>
    }

    <!-- Destination Location Selection (shown when changing to Received status) -->
    @if (showLocationSelector()) {
      <p-panel>
        <ng-template pTemplate="header">
          <div class="row-sm-start-center">
            <i class="pi pi-map-marker"></i>
            <span>{{ 'Destination_Location' | translateWF: 'Destination Location' }}</span>
          </div>
        </ng-template>
        <ng-template pTemplate="icons">
          <p-button label="{{ 'Cancel' | translateWF: 'Cancel' }}" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="cancelReceive()" />
          <p-button
            label="{{ 'Confirm_Receive' | translateWF: 'Confirm Receive' }}"
            icon="pi pi-check"
            (onClick)="confirmReceiveWithLocation()"
            [disabled]="!selectedLocationNode()"
          />
        </ng-template>
        <p-card data-variant="fields">
          <p-treeSelect
            [options]="locationTreeNodes()"
            [ngModel]="selectedLocationNode()"
            (ngModelChange)="onLocationSelected($event)"
            placeholder="{{ 'Select_Location' | translateWF: 'Select Location' }}"
            [showClear]="true"
            selectionMode="single"
            appendTo="body"
            [loading]="isLoadingLocations()"
            styleClass="w-full md:w-25rem"
          />
        </p-card>
        <ng-template pTemplate="footer">
          <p class="text-gray-600 m-0">
            {{
              'Location_Selection_Message'
                | translateWF
                  : 'Please select the destination location where the finished
          goods will be stored.'
            }}
          </p>
        </ng-template>
      </p-panel>
    }

    <!-- Download Report Button -->
    <p-button
      *ngIf="originalFinishedGoodsTransfer?.id && status.value! > Status.Draft"
      class="row-end-stretch w-full"
      label="{{ 'Finished_Goods_Transfer_Details_Download_Report' | translateWF: 'Download Report' }}"
      icon="pi pi-download"
      [rounded]="true"
      [outlined]="true"
      (click)="downloadReport()"
    >
    </p-button>

    <!-- Roll Management Section (only shown after transfer is created) -->
    @if (originalFinishedGoodsTransfer) {
      <!-- Summary Stats - only show in Draft mode -->
      @if (status.value === Status.Draft) {
        <div class="grid-3 items-stretch">
          <p-card class="card-summary">
            <div class="text-center">
              <div class="text-2xl font-bold">{{ totalRolls() }}</div>
              <div class="text-gray-500">{{ 'Total_Candidate_Rolls' | translateWF: 'Total Candidate Rolls' }}</div>
              @if (totalRollsInAnotherFgt() > 0) {
                <div class="text-xs text-gray-400 mt-1">
                  ({{ totalRollsInAnotherFgt() }}
                  {{
                    'In_Another_FGT'
                      | translateWF
                        : 'in
            another FGT'
                  }})
                </div>
              }
            </div>
          </p-card>
          <p-card class="card-summary">
            <div class="text-center">
              <div class="text-2xl font-bold text-green-600">{{ totalLoadedRolls() }}</div>
              <div class="text-gray-500">{{ 'Loaded_In_Transfer' | translateWF: 'Loaded in Transfer' }}</div>
            </div>
          </p-card>
          <p-card class="card-summary">
            <div class="text-center">
              @if (totalMissingRolls() > 0) {
                <div class="text-2xl font-bold text-red-600">{{ totalMissingRolls() }}</div>
              } @else {
                <div class="text-2xl font-bold">{{ totalMissingRolls() }}</div>
              }
              <div class="text-gray-500">{{ 'Missing' | translateWF: 'Missing' }}</div>
            </div>
          </p-card>
        </div>
      }

      <!-- Live Cuadraje (Draft only) -->
      @if (status.value === Status.Draft && liveCuadraje() && liveCuadraje()!.items.length > 0) {
        <p-panel class="mb-4" [header]="'Live_Cuadraje' | translateWF: 'Cuadraje en vivo'">
          <ng-template pTemplate="icons"> </ng-template>
          <p-card>
            @for (item of liveCuadraje()!.items; track item.fabricCode + item.productOrderNumber) {
              <div class="cuadraje-fabric-section">
                <div class="font-semibold mb-1">{{ fabricTypeLabel(item.fabricCode) }} - {{ item.fabricCode }} - {{ getFabricByCode(item.fabricCode)?.longDescription }}</div>

                <div class="row-between-stretch mb-2 text-sm">
                  <span>{{ 'Already_In_Finished_Goods' | translateWF: 'Ya en Finished Goods' }}: {{ item.alreadyPercentage | number: '1.1-1' }}%</span>
                  <span [class.text-red-600]="item.exceedsTolerance">
                    {{ 'If_Transferred_Now' | translateWF: 'Si transfiere esta PC ahora' }}: {{ item.projectedPercentage | number: '1.1-1' }}%
                  </span>
                </div>

                <csl-tolerance-meter
                  orientation="horizontal"
                  [segments]="segments(item)"
                  [scaleMin]="0"
                  [scaleMax]="scaleMax(item)"
                  [toleranceMin]="toleranceMin(item)"
                  [toleranceMax]="toleranceMax(item)"
                  [target]="item.requiredQuantityInPounds"
                />

                <div class="flex flex-wrap gap-4 mt-3 text-gray-600 text-sm">
                  <span>{{ 'Objective' | translateWF: 'Objetivo' }}: {{ item.requiredQuantityInPounds | number: '1.0-0' }} lbs</span>
                  <span>{{ 'Already_In_Finished_Goods' | translateWF: 'Ya en Finished Goods' }}: {{ item.alreadyInFinishedGoodsQuantityInPounds | number: '1.0-0' }} lbs</span>
                  <span>{{ 'This_Transfer' | translateWF: 'Esta transferencia' }}: {{ item.thisTransferQuantityInPounds | number: '1.0-0' }} lbs</span>
                  <span>{{ 'Projected' | translateWF: 'Proyectado' }}: {{ item.projectedQuantityInPounds | number: '1.0-0' }} lbs</span>
                </div>

                @if (item.exceedsTolerance) {
                  <div class="mt-4 p-3 border-round surface-100">
                    <div class="font-bold mb-2 text-yellow-700">
                      {{ 'Tolerance_Warning_Title' | translateWF: 'Advertencia de tolerancia — no bloquea la transacción' }}
                    </div>
                    <div class="mb-3 text-sm">
                      {{
                        'Tolerance_Warning_Body'
                          | translateWF
                            : 'Al intentar cambiar el estado a Draft → Ready for Transfer,
              esta transferencia dejaría'
                      }}
                      {{ fabricTypeLabel(item.fabricCode) }} - {{ item.colorReference ? getColorName(item.colorReference) : '' }}
                      {{ 'Of_Customer_PO' | translateWF: 'de la Customer PO' }} {{ item.productOrderNumber }} {{ 'At' | translateWF: 'en' }}
                      {{ item.projectedPercentage | number: '1.1-1' }}%
                      {{ 'Of_Tolerance_Target' | translateWF: 'del objetivo de tolerancia (cliente: máx.' }}
                      {{ 100 + (item.clientTolerancePercentage ?? 0) | number: '1.0-1' }}%).
                      {{ 'Informational_Only' | translateWF: 'Puede continuar — es solo informativo para Producción.' }}
                    </div>
                    <p-button severity="warn" [outlined]="true" [label]="'Continue_Anyway' | translateWF: 'Continuar de todas formas'" (onClick)="continueDespiteTolerance()" />
                  </div>
                }
              </div>
            }
          </p-card>
        </p-panel>
      }

      <!-- Loaded Rolls Table -->
      <p-panel [header]="'Loaded_Rolls' | translateWF: 'Loaded Rolls'">
        <ng-template pTemplate="icons">
          <p-tag [value]="totalLoadedRolls().toString()" severity="success" />
        </ng-template>
        <p-card data-variant="compact">
          @if (loadedRolls().length > 0) {
            <p-table [value]="loadedRolls()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'No' | translateWF: 'No.' }}</th>
                  <th>{{ 'Roll_Number' | translateWF: 'Roll Number' }}</th>
                  <th>{{ 'Fabric' | translateWF: 'Fabric' }}</th>
                  <th>{{ 'Type' | translateWF: 'Type' }}</th>
                  <th style="text-align: center">{{ 'Final_QC_IsApproved' | translateWF: 'QC' }}</th>
                  <th>{{ 'Roll_Category' | translateWF: 'Category' }}</th>
                  <th>{{ 'Final_Weight_Lb' | translateWF: 'Weight (lb)' }}</th>
                  <th>{{ 'Final_Weight_Kg' | translateWF: 'Weight (kg)' }}</th>
                  @if (!readonly()) {
                    <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-roll let-i="rowIndex">
                @let fabric = getFabricByCode(roll.expectedFabricCode);
                <tr>
                  <td>{{ i + 1 }}</td>
                  <td>{{ roll.number }}</td>
                  <td>{{ fabricFormValues[fabric?.fabricForm ?? 0] }}</td>
                  <td>{{ fabric?.longDescription }}</td>
                  <td style="text-align: center">{{ roll.finalQC?.isApproved ? ('Approved' | translateWF: 'Approved') : ('Rejected' | translateWF: 'Rejected') }}</td>
                  <td>{{ roll.rollCategory != null ? rollCategoryValues[roll.rollCategory] : '-' }}</td>
                  <td>{{ roll.finalWeight?.weightInLbs | number: '1.0-2' }}</td>
                  <td>{{ lbsToKg(roll.finalWeight?.weightInLbs ?? 0) | number: '1.0-2' }}</td>
                  @if (!readonly()) {
                    <td>
                      <p-button
                        icon="pi pi-minus"
                        [label]="'Remove' | translateWF: 'Remove'"
                        [text]="true"
                        severity="danger"
                        (onClick)="removeRoll(roll.number)"
                        [disabled]="isLoading"
                      />
                    </td>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="footer">
                <tr class="font-bold">
                  <td colspan="6" class="text-right">{{ 'Total' | translateWF: 'Total' }}:</td>
                  <td>{{ loadedRollsTotalWeightLbs() | number: '1.0-2' }}</td>
                  <td>{{ loadedRollsTotalWeightKgs() | number: '1.0-2' }}</td>
                  @if (!readonly()) {
                    <td></td>
                  }
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <p class="text-gray-500 text-center py-4">
              {{ 'No_Rolls_Loaded_Yet' | translateWF: 'No rolls loaded yet. Add rolls from the missing rolls table below.' }}
            </p>
          }
        </p-card>
      </p-panel>

      <!-- Missing Rolls Table - only if status is draft -->
      <p-panel *ngIf="status.value === Status.Draft" [header]="'Missing_Rolls' | translateWF: 'Missing Rolls'">
        <ng-template pTemplate="icons">
          @if (totalMissingRolls() > 0) {
            <p-tag [value]="totalMissingRolls().toString()" severity="danger" />
          } @else {
            <p-tag [value]="totalMissingRolls().toString()" severity="secondary" />
          }
        </ng-template>
        <p-card data-variant="compact">
          @if (missingRolls().length > 0) {
            <!-- Filters & bulk selection toolbar (Missing Rolls only) -->
            <div class="flex flex-wrap items-end gap-3 mb-4">
              <div class="form-box">
                <label>{{ 'Roll_Category' | translateWF: 'Category' }}</label>
                <p-select
                  [options]="categoryFilterOptions"
                  [ngModel]="missingRollsCategoryFilter()"
                  (ngModelChange)="missingRollsCategoryFilter.set($event)"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  placeholder="{{ 'All_Categories' | translateWF: 'All Categories' }}"
                  styleClass="w-auto"
                />
              </div>
              <div class="form-box">
                <label>{{ 'Quality' | translateWF: 'Quality' }}</label>
                <p-select
                  [options]="qualityFilterOptions"
                  [ngModel]="missingRollsQualityFilter()"
                  (ngModelChange)="missingRollsQualityFilter.set($event)"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  placeholder="{{ 'All_Qualities' | translateWF: 'All Qualities' }}"
                  styleClass="w-auto"
                />
              </div>
              <p-button
                label="{{ 'Clear_Filters' | translateWF: 'Clear Filters' }}"
                icon="pi pi-times"
                severity="secondary"
                [text]="true"
                (onClick)="clearFilters()"
              />
              @if (!readonly()) {
                <div class="row-sm ml-auto">
                  <p-button
                    label="{{ 'Select_All' | translateWF: 'Select All' }}"
                    icon="pi pi-check-square"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="selectAllFilteredMissingRolls()"
                    [disabled]="isLoading || filteredMissingRolls().length === 0"
                  />
                  <p-button
                    label="{{ 'Common_Clear' | translateWF: 'Clear' }}"
                    icon="pi pi-times"
                    severity="secondary"
                    [text]="true"
                    (onClick)="clearMissingRollsSelection()"
                    [disabled]="isLoading || selectedMissingRollsCount() === 0"
                  />
                  <p-button
                    label="{{ ('Add_Selected' | translateWF: 'Add Selected') + (selectedMissingRollsCount() > 0 ? ' (' + selectedMissingRollsCount() + ')' : '') }}"
                    icon="pi pi-plus"
                    severity="success"
                    (onClick)="addSelectedRolls()"
                    [disabled]="isLoading || selectedMissingRollsCount() === 0"
                  />
                </div>
              }
            </div>

            <p-table [value]="filteredMissingRolls()">
              <ng-template pTemplate="header">
                <tr>
                  @if (!readonly()) {
                    <th style="width: 3rem; text-align: center">
                      <p-checkbox [binary]="true" [ngModel]="allFilteredMissingRollsSelected()" (onChange)="toggleSelectAllFilteredMissingRolls($event.checked)" />
                    </th>
                  }
                  <th>{{ 'No' | translateWF: 'No.' }}</th>
                  <th>{{ 'Roll_Number' | translateWF: 'Roll Number' }}</th>
                  <th>{{ 'Type' | translateWF: 'Type' }}</th>
                  <th>{{ 'Fabric' | translateWF: 'Fabric' }}</th>
                  <th style="text-align: center">{{ 'Final_QC_IsApproved' | translateWF: 'QC' }}</th>
                  <th>{{ 'Roll_Category' | translateWF: 'Category' }}</th>
                  <th>{{ 'Final_Weight_Lb' | translateWF: 'Weight (lb)' }}</th>
                  <th>{{ 'Final_Weight_Kg' | translateWF: 'Weight (kg)' }}</th>
                  @if (!readonly()) {
                    <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-roll let-i="rowIndex">
                @let fabric = getFabricByCode(roll.expectedFabricCode);
                <tr>
                  @if (!readonly()) {
                    <td style="text-align: center">
                      <p-checkbox [binary]="true" [ngModel]="isMissingRollSelected(roll.number)" (onChange)="toggleMissingRollSelection(roll.number, $event.checked)" />
                    </td>
                  }
                  <td>{{ i + 1 }}</td>
                  <td>{{ roll.number }}</td>
                  <td>{{ fabricFormValues[fabric?.fabricForm ?? 0] }}</td>
                  <td>{{ fabric?.longDescription }}</td>
                  <td style="text-align: center">{{ roll.finalQC?.isApproved ? ('Approved' | translateWF: 'Approved') : ('Rejected' | translateWF: 'Rejected') }}</td>
                  <td>{{ roll.rollCategory != null ? rollCategoryValues[roll.rollCategory] : '-' }}</td>
                  <td>{{ roll.finalWeight?.weightInLbs | number: '1.0-2' }}</td>
                  <td>{{ lbsToKg(roll.finalWeight?.weightInLbs ?? 0) | number: '1.0-2' }}</td>
                  @if (!readonly()) {
                    <td>
                      <p-button
                        icon="pi pi-plus"
                        [label]="'Add' | translateWF: 'Add'"
                        [text]="true"
                        severity="success"
                        (onClick)="addRollToTransaction(roll.number)"
                        [disabled]="isLoading"
                      />
                    </td>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="footer">
                <tr class="font-bold">
                  <td [attr.colspan]="readonly() ? 6 : 7" class="text-right">{{ 'Total' | translateWF: 'Total' }}:</td>
                  <td>{{ filteredMissingRollsTotalWeightLbs() | number: '1.0-2' }}</td>
                  <td>{{ filteredMissingRollsTotalWeightKgs() | number: '1.0-2' }}</td>
                  @if (!readonly()) {
                    <td></td>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="readonly() ? 8 : 10" class="text-gray-500 text-center py-4">
                    {{ 'No_Missing_Rolls_Match_Filters' | translateWF: 'No rolls match the selected filters.' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <p class="text-green-600 text-center py-4 font-semibold">
              {{ 'All_Rolls_Loaded' | translateWF: 'All rolls have been loaded!' }}
            </p>
          }
        </p-card>
      </p-panel>
    }
  </div>
</main>
